import React, { useEffect, useState } from "react"
import { SubmitButton } from "../../shared/Button"
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js"
import CardSection from "../../tips/CardSection"
import { formatPrice } from "../../../util/ticketUtils"
import { useRollbar } from "@rollbar/react"
import { useUpdateTicketPurchase } from "../../../hooks/useTicketPurchaseMutations"
import { useQueryClient } from "react-query"
import { ticketPurchaseQueryKeys } from "../../../hooks/useTicketPurchase"
import Pluralize from "react-pluralize"

const TICKET_PURCHASE_STATUS_CONFIRMED_BY_USER = "confirmed_by_user"

interface CheckoutFormProps {
  userName: string
  userEmail?: string
  lastFour: string
  setLastFour: (lastFour: string) => void
  bandId: number
  ticketPurchaseId?: number
  paymentMethod?: string | null
  clientSecret?: string
  ticketPrice: number
  quantity?: number
  postId: number
  isNewCard: boolean
  setIsNewCard: (isNewCard: boolean) => void
  onSwitchToNewCard?: () => void
  onCheckoutComplete?: () => void
  isCreatingNewCard?: boolean // Indicates if parent is creating/updating ticket purchase for new card
  onBeforeCheckout?: () => Promise<{ id: number; payment_method?: string | null; client_secret?: string }>
}

interface TicketPurchaseResponse {
  ticket_purchase: {
    last_four: string
    ticket_id?: string
  }
}

export const CheckoutForm: React.FC<CheckoutFormProps> = ({
  userName,
  userEmail,
  lastFour,
  setLastFour,
  bandId,
  postId,
  ticketPurchaseId,
  paymentMethod = null,
  clientSecret,
  ticketPrice,
  quantity = 1,
  isNewCard,
  setIsNewCard,
  onSwitchToNewCard,
  onCheckoutComplete,
  isCreatingNewCard = false,
  onBeforeCheckout,
}) => {
  const [checkoutCompleted, setCheckoutCompleted] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [saveCard, setSaveCard] = useState<boolean>(true)
  const stripe = useStripe()
  const elements = useElements()
  const rollbar = useRollbar()
  const updateMutation = useUpdateTicketPurchase()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (updateMutation.status === "error") {
      console.error("Mutation error:", updateMutation.error)
      rollbar.error("Error updating ticket purchase in mutation", {
        error: updateMutation.error,
      })
      setIsSubmitting(false)
      setErrorMessage(
        "Sorry, something went wrong. Please reload the page and try again",
      )
    }
  }, [updateMutation.status, updateMutation.error, rollbar])

  useEffect(() => {
    if (
      updateMutation.status === "success" &&
      updateMutation.data?.success === false
    ) {
      setIsSubmitting(false)
      const errCode = updateMutation.data?.error
      setErrorMessage(
        errCode === "payment_failed"
          ? "The payment failed to go through. Please try a different payment method."
          : errCode || "Payment failed. Please try again.",
      )
    }
  }, [updateMutation.status, updateMutation.data])

  // update ticket purchase status upon successful payment submission
  const handleUpdateTicketPurchaseResolved = (purchaseId: number, method: string) => {
    updateMutation.mutate({
      bandId,
      ticketPurchaseId: purchaseId,
      params: {
        ticket_purchase: {
          status: TICKET_PURCHASE_STATUS_CONFIRMED_BY_USER,
          card_confirmed: true,
          payment_method: method,
          quantity: quantity,
          save_card: saveCard,
        },
      },
    })
  }

  const handleUpdateTicketPurchase = async (paymentMethod: string) => {
    if (!ticketPurchaseId) return
    handleUpdateTicketPurchaseResolved(ticketPurchaseId, paymentMethod)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)

    let resolvedPurchaseId = ticketPurchaseId
    let resolvedPaymentMethod = paymentMethod
    let resolvedClientSecret = clientSecret

    if (onBeforeCheckout) {
      try {
        const ticket = await onBeforeCheckout()
        resolvedPurchaseId = ticket.id
        resolvedPaymentMethod = ticket.payment_method ?? null
        resolvedClientSecret = ticket.client_secret
      } catch {
        setIsSubmitting(false)
        setErrorMessage("Something went wrong. Please try again.")
        return
      }
    }

    // If isNewCard is true, always use the card input form (ignore paymentMethod)
    // This handles the case where user clicks "Use a different card" but paymentMethod
    // hasn't been cleared yet from the parent component's state
    if (resolvedPaymentMethod && !isNewCard) {
      handleUpdateTicketPurchaseResolved(resolvedPurchaseId!, resolvedPaymentMethod)
    } else {
      if (!stripe || !elements) {
        // Stripe.js has not yet loaded. Disable Form
        setIsSubmitting(false)
        setErrorMessage(
          "Payment form is still loading. Please wait a moment and try again.",
        )
        return
      }
      if (!resolvedClientSecret) {
        setIsSubmitting(false)
        setErrorMessage(
          "Payment form is not ready. Please wait a moment and try again.",
        )
        return
      }
      setErrorMessage("")
      try {
        const result = await stripe.confirmCardSetup(resolvedClientSecret!, {
          payment_method: {
            card: elements.getElement(CardElement)!,
            billing_details: {
              name: userName,
            },
          },
        })

        if (result.error) {
          // Show error to your customer (e.g., insufficient funds, setup intent already confirmed)
          setIsSubmitting(false)
          setErrorMessage(
            "There was an error with your card: " +
              result.error.message +
              ". Please double check it, and resubmit",
          )
        } else if (result.setupIntent && result.setupIntent.payment_method) {
          handleUpdateTicketPurchaseResolved(
            resolvedPurchaseId!,
            result.setupIntent.payment_method as string,
          )
        } else {
          setIsSubmitting(false)
          setErrorMessage("Payment method was not created. Please try again.")
        }
      } catch (error: any) {
        // Handle network errors or other exceptions
        setIsSubmitting(false)
        console.error("Error confirming card setup:", error)
        setErrorMessage(
          error?.message ||
            "An error occurred while processing your card. Please try again.",
        )
      }
    }
  }

  useEffect(() => {
    if (updateMutation.status === "success" && updateMutation.data?.success) {
      // Handle array response (ticket_purchases) or single response (ticket_purchase)
      const firstPurchase =
        updateMutation.data.ticket_purchases?.[0] ||
        updateMutation.data.ticket_purchase
      if (firstPurchase) {
        // The response includes user_default_payment_method_last_four when @include_last_four is true
        const lastFourValue =
          firstPurchase.user_default_payment_method_last_four ||
          firstPurchase.last_four
        if (lastFourValue) {
          setLastFour(lastFourValue)
        }

        // Invalidate myTickets cache so UI reflects the new purchase
        queryClient.invalidateQueries(ticketPurchaseQueryKeys.myTickets(postId))
        // Invalidate user query so UserContext refreshes with updated payment method
        // Use predicate to match the exact query key format used in HomeApp
        // The backend now reloads the user after saving, so the response includes updated payment method
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey
            return Array.isArray(key) && key[0] === "user"
          },
        })
        // Refetch immediately to get the updated user data
        queryClient.refetchQueries({
          predicate: (query) => {
            const key = query.queryKey
            return Array.isArray(key) && key[0] === "user"
          },
        })
      }
      setCheckoutCompleted(true)
      // Notify parent component that checkout is complete
      onCheckoutComplete?.()
    }
  }, [
    updateMutation.status,
    updateMutation.data,
    setLastFour,
    queryClient,
    postId,
    onCheckoutComplete,
  ])

  const ticketPurchaseCount = updateMutation.data?.ticket_purchases?.length || 0

  return (
    <>
      {checkoutCompleted ? (
        <div className="pt-4">
          <div className="text-green-600 font-bold text-lg mb-2">
            <Pluralize
              singular={"Ticket"}
              plural={"Tickets"}
              count={ticketPurchaseCount}
            />{" "}
            Purchased Successfully!{" "}
            <span role="img" aria-label="celebration">
              🎉
            </span>
          </div>
          <div className="text-sm text-gray-600">
            Your {ticketPurchaseCount === 1 ? "ticket has" : "tickets have"}{" "}
            been purchased and you've been automatically RSVP'd to this event.
            Check your email for ticket details and receipt.
          </div>
          {(() => {
            const purchases = updateMutation.data?.ticket_purchases || []
            const purchasesWithTicketId = purchases.filter(
              (p: any) => p?.ticket_id,
            )

            return (
              purchasesWithTicketId.length > 0 && (
                <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                  {userName && (
                    <div className="text-lg font-semibold text-green-800 mb-1">
                      {userName}
                    </div>
                  )}
                  {userEmail && (
                    <div className="text-sm text-green-800 mb-2">
                      {userEmail}
                    </div>
                  )}
                  {purchasesWithTicketId.map((purchase: any, index: number) => (
                    <div
                      key={purchase.id || index}
                      className="text-sm font-mono text-green-800"
                    >
                      {index + 1}. {purchase.ticket_id}
                    </div>
                  ))}
                </div>
              )
            )
          })()}
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {paymentMethod && !isNewCard ? (
            <>
              <div className="mb-4 italic mb-4 text-gray-900">
                Your card ending in&nbsp;
                <span className="font-bold text-gray-800">{lastFour}</span>
                &nbsp;will be used for payment
                <button
                  type="button"
                  className="focus:outline-none italic ml-3 text-blue-600 text-sm"
                  onClick={() => {
                    setIsNewCard(true)
                    onSwitchToNewCard?.()
                  }}
                >
                  Use a different card
                </button>
              </div>
              {errorMessage ? (
                <div className="text-red-500 text-sm mb-4">{errorMessage}</div>
              ) : null}
              <SubmitButton
                bg="bg-green-700"
                type="submit"
                active={!isSubmitting}
                className=""
              >
                Confirm Purchase ({formatPrice(ticketPrice * quantity)})
              </SubmitButton>
            </>
          ) : onBeforeCheckout && !isNewCard && lastFour ? (
            // Sliding scale with saved card — ticket not created yet
            <>
              <div className="mb-4 italic text-gray-900">
                Your card ending in&nbsp;
                <span className="font-bold text-gray-800">{lastFour}</span>
                &nbsp;will be used for payment
                <button
                  type="button"
                  className="focus:outline-none italic ml-3 text-blue-600 text-sm"
                  onClick={() => { setIsNewCard(true); onSwitchToNewCard?.() }}
                >
                  Use a different card
                </button>
              </div>
              {errorMessage && <div className="text-red-500 text-sm mb-4">{errorMessage}</div>}
              <SubmitButton bg="bg-green-700" type="submit" active={!isSubmitting} className="">
                {isSubmitting ? "Processing..." : `Confirm Purchase (${formatPrice(ticketPrice * quantity)})`}
              </SubmitButton>
            </>
          ) : onBeforeCheckout && stripe && elements ? (
            // Sliding scale with new card — ticket not created yet, collect card now
            <>
              {errorMessage && <div className="text-red-500 text-sm mb-4">{errorMessage}</div>}
              <CardSection />
              <label className="flex items-center gap-2 text-sm text-gray-700 mt-3 mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveCard}
                  onChange={(e) => setSaveCard(e.target.checked)}
                />
                Save card for future purchases
              </label>
              <SubmitButton
                bg="bg-green-700"
                type="submit"
                active={!isSubmitting}
                className=""
              >
                {isSubmitting ? "Processing..." : `Confirm Purchase (${formatPrice(ticketPrice * quantity)})`}
              </SubmitButton>
              <div className="text-sm text-gray-600 mt-4">
                I authorize bandNada to send instructions to the financial
                institution that issued my card to take payments from my card
                that I explicitly confirmed in bandNada
              </div>
            </>
          ) : isNewCard && isCreatingNewCard && !clientSecret ? (
            // Show loading state when switching to new card but clientSecret isn't ready yet
            <div className="text-gray-600">
              <div className="mb-2">Preparing payment form...</div>
              <div className="text-sm">
                Please wait while we set up a new payment method.
              </div>
            </div>
          ) : clientSecret && stripe && elements ? (
            <>
              {errorMessage ? (
                <div className="text-red-500 text-sm mb-4">{errorMessage}</div>
              ) : null}
              <CardSection />
              <label className="flex items-center gap-2 text-sm text-gray-700 mt-3 mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveCard}
                  onChange={(e) => setSaveCard(e.target.checked)}
                />
                Save card for future purchases
              </label>
              <SubmitButton
                bg="bg-green-700"
                type="submit"
                active={!isSubmitting && !updateMutation.isLoading}
                className=""
              >
                {isSubmitting || updateMutation.isLoading
                  ? "Processing..."
                  : `Confirm Purchase (${formatPrice(ticketPrice * quantity)})`}
              </SubmitButton>
              <div className="text-sm text-gray-600 mt-4">
                I authorize bandNada to send instructions to the financial
                institution that issued my card to take payments from my card
                that I explicitly confirmed in bandNada
              </div>
            </>
          ) : (
            <div>Loading payment form...</div>
          )}
        </form>
      )}
    </>
  )
}

export default CheckoutForm
